import { atom } from 'jotai';
import { isStreamingContentAtom } from '.';
import { mapDataAtom } from '.';
import { pass2LearningPathAtom } from '.';
import { isStreamingMapAtom } from '.';
import { slugify } from '../lib/map/slugify';
import { Chapter, Pass2Section, Pass2Option, Pass2Sidequest } from '../typesV2';

export const chapterAtom = atom<Chapter | undefined>(undefined);

type TopicItem = Pass2Section | Pass2Option | Pass2Sidequest;

const getTopicItems = (topic: any): TopicItem[] | undefined => {
  if (topic?.options) return topic.options;
  const sections = topic?.sections || [];
  const sidequests = topic?.sideQuests || [];
  return [...sections, ...sidequests];
};

const getItemTitle = (item: TopicItem): string => {
  if ('subTopicTitle' in item) return item.subTopicTitle;
  if ('optionTitle' in item) return item.optionTitle;
  return item.sideQuestTitle;
};

const getFirstItem = (topic: any): TopicItem | undefined => {
  const items = getTopicItems(topic);
  return items?.[0];
};

const getNextItem = (topic: any, currentIndex: number): TopicItem | undefined => {
  const items = getTopicItems(topic);
  return items?.[currentIndex + 1];
};

const getPrevItem = (topic: any, currentIndex: number): TopicItem | undefined => {
  const items = getTopicItems(topic);
  return items?.[currentIndex - 1];
};

const getLastItem = (topic: any): TopicItem | undefined => {
  const items = getTopicItems(topic);
  return items?.[items.length - 1];
};

const isSideQuest = (item: TopicItem): item is Pass2Sidequest => {
  return 'sideQuestTitle' in item;
};

const isOption = (item: TopicItem): item is Pass2Option => {
  return 'optionTitle' in item;
};

export const nextChapterAtom = atom((get) => {
  const chapter = get(chapterAtom);
  const isStreamingMap = get(isStreamingMapAtom);
  // const mapData = get(mapDataAtom);
  const pass2LearningPath = get(pass2LearningPathAtom);

  if (isStreamingMap || !pass2LearningPath) return null;

  if (chapter?.type === 'overview') {
    const firstTopic = pass2LearningPath?.[0];
    const firstItem = getFirstItem(firstTopic);
    if (!firstTopic || !firstItem) return null;
    
    return {
      type: isSideQuest(firstItem) ? 'sidequest' : isOption(firstItem) ? 'option' : 'section',
      topicIndex: 0,
      subtopicIndex: 0
    };
  }

  if (!chapter) return null;
  const type = chapter.type;

  // If we're on whats-next, there is no next chapter
  if (type === 'whats-next') return null;

  if (type !== 'section' && type !== 'option' && type !== 'sidequest') return null;
  
  const { topicIndex, subtopicIndex } = chapter;
  const currentTopic = pass2LearningPath[topicIndex];
  const items = getTopicItems(currentTopic);
  if (!items) return null;

  // If we're on a sidequest
  if (type === 'sidequest') {
    if (subtopicIndex < (currentTopic.sideQuests?.length ?? 0) - 1) {
      return {
        type: 'sidequest',
        topicIndex,
        subtopicIndex: subtopicIndex + 1
      };
    } else if (topicIndex < pass2LearningPath.length - 1) {
      const nextTopic = pass2LearningPath[topicIndex + 1];
      const nextItem = getFirstItem(nextTopic);
      if (!nextTopic || !nextItem) return null;

      return {
        type: isSideQuest(nextItem) ? 'sidequest' : isOption(nextItem) ? 'option' : 'section',
        topicIndex: topicIndex + 1,
        subtopicIndex: 0
      };
    } else {
      // We're at the last topic, check if we should go to whats-next
      const lastItem = getLastItem(currentTopic);
      if (lastItem && subtopicIndex === (currentTopic.sideQuests?.length ?? 0) - 1) {
        return {
          type: 'whats-next'
        };
      }
    }
    return null;
  }

  // If we're on a section
  if (type === 'section') {
    if (subtopicIndex < (currentTopic.sections?.length ?? 0) - 1) {
      return {
        type: 'section',
        topicIndex,
        subtopicIndex: subtopicIndex + 1
      };
    } else if (currentTopic.sideQuests?.length) {
      // Move to first sidequest
      return {
        type: 'sidequest',
        topicIndex,
        subtopicIndex: 0
      };
    } else if (topicIndex < pass2LearningPath.length - 1) {
      const nextTopic = pass2LearningPath[topicIndex + 1];
      const nextItem = getFirstItem(nextTopic);
      if (!nextTopic || !nextItem) return null;

      return {
        type: isSideQuest(nextItem) ? 'sidequest' : isOption(nextItem) ? 'option' : 'section',
        topicIndex: topicIndex + 1,
        subtopicIndex: 0
      };
    } else {
      // We're at the last topic, check if we should go to whats-next
      const lastItem = getLastItem(currentTopic);
      if (lastItem && subtopicIndex === (currentTopic.sections?.length ?? 0) - 1) {
        return {
          type: 'whats-next'
        };
      }
    }
    return null;
  }

  // If we're on an option
  if (type === 'option') {
    if (subtopicIndex < (currentTopic.options?.length ?? 0) - 1) {
      return {
        type: 'option',
        topicIndex,
        subtopicIndex: subtopicIndex + 1
      };
    } else if (topicIndex < pass2LearningPath.length - 1) {
      const nextTopic = pass2LearningPath[topicIndex + 1];
      const nextItem = getFirstItem(nextTopic);
      if (!nextTopic || !nextItem) return null;

      return {
        type: isSideQuest(nextItem) ? 'sidequest' : isOption(nextItem) ? 'option' : 'section',
        topicIndex: topicIndex + 1,
        subtopicIndex: 0
      };
    } else {
      // We're at the last topic, check if we should go to whats-next
      const lastItem = getLastItem(currentTopic);
      if (lastItem && subtopicIndex === (currentTopic.options?.length ?? 0) - 1) {
        return {
          type: 'whats-next'
        };
      }
    }
    return null;
  }

  return null;
});

export const prevChapterAtom = atom((get) => {
  const chapter = get(chapterAtom);
  const isStreamingMap = get(isStreamingMapAtom);
  // const mapData = get(mapDataAtom);
  const pass2LearningPath = get(pass2LearningPathAtom);

  if (isStreamingMap || !pass2LearningPath) return null;

  if (chapter?.type === 'overview') return null;

  if (!chapter) return null;
  const type = chapter.type;

  // If we're on whats-next, go to the last item of the last topic
  if (type === 'whats-next') {
    const lastTopic = pass2LearningPath[pass2LearningPath.length - 1];
    if (!lastTopic) return null;

    const lastOptionIndex = (lastTopic?.options?.length ?? 0) - 1;
    const lastSideQuestIndex = (lastTopic?.sideQuests?.length ?? 0) - 1;
    const lastSectionIndex = (lastTopic?.sections?.length ?? 0) - 1;

    if (lastOptionIndex >= 0) {
      return {
        type: 'option',
        topicIndex: pass2LearningPath.length - 1,
        subtopicIndex: lastOptionIndex
      };
    } else if (lastSideQuestIndex >= 0) {
      return {
        type: 'sidequest',
        topicIndex: pass2LearningPath.length - 1,
        subtopicIndex: lastSideQuestIndex
      };
    } else if (lastSectionIndex >= 0) {
      return {
        type: 'section',
        topicIndex: pass2LearningPath.length - 1,
        subtopicIndex: lastSectionIndex
      };
    }
    return null;
  }

  if (type !== 'section' && type !== 'option' && type !== 'sidequest') return null;
  
  const { topicIndex, subtopicIndex } = chapter;
  const currentTopic = pass2LearningPath[topicIndex];
  const items = getTopicItems(currentTopic);
  if (!items) return null;

  // If we're on a sidequest
  if (type === 'sidequest') {
    if (subtopicIndex > 0) {
      return {
        type: 'sidequest',
        topicIndex,
        subtopicIndex: subtopicIndex - 1
      };
    } else {
      // Move to last section of current topic
      const lastSectionIndex = (currentTopic.sections?.length ?? 0) - 1;
      if (lastSectionIndex >= 0) {
        return {
          type: 'section',
          topicIndex,
          subtopicIndex: lastSectionIndex
        };
      }
    }
  }

  // If we're on a section
  if (type === 'section') {
    if (subtopicIndex > 0) {
      return {
        type: 'section',
        topicIndex,
        subtopicIndex: subtopicIndex - 1
      };
    } else if (topicIndex > 0) {
      const prevTopic = pass2LearningPath[topicIndex - 1];
      const lastSideQuestIndex = (prevTopic.sideQuests?.length ?? 0) - 1;
      const lastSectionIndex = (prevTopic.sections?.length ?? 0) - 1;
      const lastOptionIndex = (prevTopic.options?.length ?? 0) - 1;
      
      if (lastSideQuestIndex >= 0) {
        return {
          type: 'sidequest',
          topicIndex: topicIndex - 1,
          subtopicIndex: lastSideQuestIndex
        };
      } else if (lastSectionIndex >= 0) {
        return {
          type: 'section',
          topicIndex: topicIndex - 1,
          subtopicIndex: lastSectionIndex
        };
      } else if (lastOptionIndex >= 0) {
        return {
          type: 'option',
          topicIndex: topicIndex - 1,
          subtopicIndex: lastOptionIndex
        };
      }
    }
  }

  // If we're on an option
  if (type === 'option') {
    if (subtopicIndex > 0) {
      return {
        type: 'option',
        topicIndex,
        subtopicIndex: subtopicIndex - 1
      };
    } else if (topicIndex > 0) {
      const prevTopic = pass2LearningPath[topicIndex - 1];
      const lastSideQuestIndex = (prevTopic.sideQuests?.length ?? 0) - 1;
      const lastSectionIndex = (prevTopic.sections?.length ?? 0) - 1;
      
      if (lastSideQuestIndex >= 0) {
        return {
          type: 'sidequest',
          topicIndex: topicIndex - 1,
          subtopicIndex: lastSideQuestIndex
        };
      } else if (lastSectionIndex >= 0) {
        return {
          type: 'section',
          topicIndex: topicIndex - 1,
          subtopicIndex: lastSectionIndex
        };
      }
    }
  }

  return null;
});
